import { useState, useEffect, useCallback } from 'react'
import { AnimatePresence } from 'framer-motion'
import Presentation from './components/Presentation'
import Navigation from './components/Navigation'
import KeyboardHints from './components/KeyboardHints'
import ParticleBackground from './components/Animations/ParticleBackground'

// Import all slides
import WelcomeSlide from './slides/WelcomeSlide'
import TitleSlide from './slides/TitleSlide'
import EvolutionSlide from './slides/EvolutionSlide'
import LLMExplainerSlide from './slides/LLMExplainerSlide'
import WorkflowOverviewSlide from './slides/WorkflowOverviewSlide'
import ToolsLandscapeSlide from './slides/ToolsLandscapeSlide'
import IntegrationPointsSlide from './slides/IntegrationPointsSlide'
import CodeStructureSlide from './slides/CodeStructureSlide'
import ClaudeMdSlide from './slides/ClaudeMdSlide'
import ProductivityMetricsSlide from './slides/ProductivityMetricsSlide'
import PromptingLanguageSlide from './slides/PromptingLanguageSlide'
import WhyLearnPromptingSlide from './slides/WhyLearnPromptingSlide'
import BestPracticesSlide from './slides/BestPracticesSlide'
import ChallengesSolutionsSlide from './slides/ChallengesSolutionsSlide'
import FutureTrendsSlide from './slides/FutureTrendsSlide'
import CallToActionSlide from './slides/CallToActionSlide'

const slides = [
  { id: 'welcome', component: WelcomeSlide, title: 'Welcome' },
  { id: 'title', component: TitleSlide, title: 'AI Coding Workflows' },
  { id: 'evolution', component: EvolutionSlide, title: 'The Evolution' },
  { id: 'llm', component: LLMExplainerSlide, title: 'Understanding LLMs' },
  { id: 'tools', component: ToolsLandscapeSlide, title: 'AI Tools Landscape' },
  { id: 'integration', component: IntegrationPointsSlide, title: 'Integration Points' },
  { id: 'structure', component: CodeStructureSlide, title: 'Code Structure' },
  { id: 'claudemd', component: ClaudeMdSlide, title: 'CLAUDE.md Best Practices' },
  { id: 'workflow', component: WorkflowOverviewSlide, title: 'Workflow Overview' },
  { id: 'prompting', component: PromptingLanguageSlide, title: 'Prompting Language' },
  { id: 'why-prompting', component: WhyLearnPromptingSlide, title: 'The Prompting Advantage' },
  { id: 'metrics', component: ProductivityMetricsSlide, title: 'Productivity Metrics' },
  { id: 'practices', component: BestPracticesSlide, title: 'Best Practices' },
  { id: 'challenges', component: ChallengesSolutionsSlide, title: 'Challenges & Solutions' },
  { id: 'future', component: FutureTrendsSlide, title: 'Future Trends' },
  { id: 'cta', component: CallToActionSlide, title: 'Get Started' },
]

function App() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [direction, setDirection] = useState(0)
  const [isAutoPlay, setIsAutoPlay] = useState(false)
  const [autoPlayInterval, setAutoPlayInterval] = useState(8000)

  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < slides.length) {
      setDirection(index > currentSlide ? 1 : -1)
      setCurrentSlide(index)
    }
  }, [currentSlide])

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setDirection(1)
      setCurrentSlide(prev => prev + 1)
    }
  }, [currentSlide])

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1)
      setCurrentSlide(prev => prev - 1)
    }
  }, [currentSlide])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault()
          nextSlide()
          break
        case 'ArrowLeft':
          e.preventDefault()
          prevSlide()
          break
        case 'Home':
          e.preventDefault()
          goToSlide(0)
          break
        case 'End':
          e.preventDefault()
          goToSlide(slides.length - 1)
          break
        case 'Escape':
          e.preventDefault()
          setIsAutoPlay(false)
          break
        case 'a':
          e.preventDefault()
          setIsAutoPlay(prev => !prev)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextSlide, prevSlide, goToSlide])

  // Touch/swipe support
  useEffect(() => {
    let touchStartX = 0
    let touchEndX = 0

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX
    }

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX = e.changedTouches[0].screenX
      const diff = touchStartX - touchEndX

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          nextSlide()
        } else {
          prevSlide()
        }
      }
    }

    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [nextSlide, prevSlide])

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlay) return

    const interval = setInterval(() => {
      if (currentSlide < slides.length - 1) {
        nextSlide()
      } else {
        setIsAutoPlay(false)
      }
    }, autoPlayInterval)

    return () => clearInterval(interval)
  }, [isAutoPlay, currentSlide, autoPlayInterval, nextSlide])

  const CurrentSlideComponent = slides[currentSlide].component

  return (
    <div className="h-screen w-screen overflow-hidden bg-space-900 relative">
      {/* Animated background */}
      <ParticleBackground />

      {/* Aurora gradient overlay */}
      <div className="absolute inset-0 aurora-bg opacity-30 pointer-events-none" />

      {/* Main presentation area */}
      <main className="relative z-10 h-full w-full">
        <AnimatePresence mode="wait" custom={direction}>
          <Presentation key={currentSlide} direction={direction}>
            <CurrentSlideComponent isActive={true} />
          </Presentation>
        </AnimatePresence>
      </main>

      {/* Navigation */}
      <Navigation
        currentSlide={currentSlide}
        totalSlides={slides.length}
        slides={slides}
        onSlideChange={goToSlide}
        onNext={nextSlide}
        onPrev={prevSlide}
      />

      {/* Keyboard hints */}
      <KeyboardHints />

      {/* Auto-play controls */}
      <div className="fixed bottom-6 left-6 z-50 flex items-center gap-3">
        <button
          onClick={() => setIsAutoPlay(!isAutoPlay)}
          className={`glass px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
            isAutoPlay ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/60 hover:text-white'
          }`}
        >
          {isAutoPlay ? 'Pause' : 'Auto-play'}
        </button>
        {isAutoPlay && (
          <select
            value={autoPlayInterval}
            onChange={(e) => setAutoPlayInterval(Number(e.target.value))}
            className="glass px-3 py-2 rounded-full text-sm bg-transparent text-white/60 border-none outline-none cursor-pointer"
          >
            <option value={5000} className="bg-space-800">5s</option>
            <option value={8000} className="bg-space-800">8s</option>
            <option value={12000} className="bg-space-800">12s</option>
          </select>
        )}
      </div>

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default App
